import { ApplyPromocodeResponseType } from "@entities/cart/types"
import { AxiosRequestConfig } from "axios"

import { privateFetch, publicFetch } from "../libs/axios"
import {
  AddParticipantsToCartRequestType,
  AddParticipantsToCartResponseType,
  ApplyPromocodeRequestType,
  CalculateOrderTipRequestType,
  CalculateOrderTipResponseType,
  CreateCartRequestType,
  CreateCartResponseType,
  CreateEditExpenseDiscountRequestType,
  GetCartResponseType,
  GetParticipantsFromCart,
  GetPerBookingFieldsOfCartResponseType,
  GetPerOrderFieldsResponseType,
  UpdateParticipantsInCartRequestType,
  UpdatePerBookingFieldsOfCartRequestType,
  UpdatePerBookingFieldsOfCartResponseType,
  UpdatePerBookingFieldsOfOrderRequestType,
  UpdatePerOrderFieldsOfCartRequestType,
  UpdatePerOrderFieldsOfCartResponseType,
  UpdatePerOrderFieldsOfOrderRequestType,
  ValidateGiftcardCodeResponseType,
} from "../types/CartTypes"
import { DomainResponseType } from "../types/DomainTypes"
import {
  AddAddonToOrderItemRequestType,
  AddCustomerRequestType,
  AddCustomerResponseType,
  AddExpenseDiscountToOrderItemRequestType,
  AddMultipleAddonToOrderItemRequestType,
  ApplyMembershipToCartRequestType,
  ApplyMembershipToCartResponseType,
  AuthorizeCardToHoldAmountRequestType,
  AuthorizeCardToHoldAmountResponseType,
  CreateOrderRequestType,
  CreateOrderResponseType,
  CustomerMembershipResponseType,
  DiscardCartResponseType,
  GetActionsLogResponseType,
  GetAllOrdersResponseType,
  GetAllProcutAvailibleResponseType,
  GetAvailabilityForDateResponseType,
  GetAvailabilityForMonthResponseType,
  GetAvailabilityResponseType,
  GetCategoriesResponseType,
  GetCustomersResponseType,
  GetLiveCapacityByProductIdPayload,
  GetLiveCapacityByProductIdResponseType,
  GetMembershipDiscountsResponseType,
  GetOrderCheckInDetailsResponseType,
  GetOrderDetailsResponseType,
  GetParticipantsOfOrderItemResponseType,
  GetPerParticipantFieldsOfOrderResponseType,
  GetProductFieldsResponseType,
  GetRefundMethodsType,
  GetSavedCardsOfCustomerResponseType,
  IssueGiftcardForRefundRequestType,
  GetTransactionsResponseType,
  IPaymentHoldType,
  MembershipUsageResponseType,
  PayOrderRequestType,
  RefundFormAttributes,
  SaveCartToOrderRequestType,
  TransactionType,
  UpdateAddonOfOrderItemRequestType,
  UpdateOrderAffiliateRequestType,
  UpdateOrderCheckInStatusResponseType,
  UpdateOrderCustomerRequestType,
  UpdateOrderRequestType,
  UpdateOrderStatusRequestType,
  UpdateOrderStatusResponseType,
  UpdateParticipantCheckInStatusRequestType,
  UpdateParticipantCheckInStatusResponseType,
  UpdatePerParticipantFieldsOfOrderRequestType,
  UpdatePriceOfOrderItemRequestType,
  UpdateQuantityOfOrderItemRequestType,
  getUnitsWithLiveCapacityRequestType,
} from "../types/OrderTypes"
import { jsonToQueryString } from "../utils/Common"

export const getCategoriesWithProducts = async (isIntegratedPage?: boolean) =>
  await privateFetch().get<GetCategoriesResponseType>(
    `/api/v1${isIntegratedPage ? "/public" : ""}/categories?include=products&filter[type]=product&paginate=false`
  )

export const getProductAvailability = async (
  productId: string,
  isIntegratedPage?: boolean | undefined
) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetCategoriesResponseType>(
      `/api/v1/public/product/${productId}/availability`
    )
  }
  return await privateFetch().get<GetAvailabilityResponseType>(
    `/api/v1/product/${productId}/availability`
  )
}

export const getActiveProducts = async (isIntegratedPage?: boolean, customerId = "") => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetCategoriesResponseType["data"]>(
      `/api/v1/public/categories?include=activeProducts&filter[type]=product&paginate=0&includeMembershipDetails=1&customer_id=${customerId}`
    )
  }
  return await privateFetch().get<GetCategoriesResponseType["data"]>(
    `/api/v1/categories?include=activeProducts&filter[type]=product&paginate=0&includeMembershipDetails=1&customer_id=${customerId}&getActiveProducts=true`
  )
}

export const getAllProductAvailibility = async (isIntegratedPage?: boolean | undefined) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetAllProcutAvailibleResponseType[]>(
      `/api/v1/public/products-availability/month?active_products=true`
    )
  }
  return await privateFetch().get<GetAllProcutAvailibleResponseType[]>(
    `/api/v1/products-availability/month?active_products=true`
  )
}

export const getProductAvailabilityForMonth = async (
  productId: string,
  date?: string,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetAvailabilityForMonthResponseType>(
      `/api/v1/public/product/${productId}/availability/calendar/${date ? `?date=${date}` : ""}`
    )
  }
  return await privateFetch().get<GetAvailabilityForMonthResponseType>(
    `/api/v1/product/${productId}/availability/calendar/${date ? `?date=${date}` : ""}`
  )
}

export const getProductAvailabilityForDate = async ({
  productId,
  date,
  isIntegratedPage = false,
}: {
  productId: string
  date: string
  isIntegratedPage?: boolean | undefined
}) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetAvailabilityForDateResponseType>(
      `/api/v1/public/product/${productId}/availability/check?date=${date}`
    )
  }
  return await privateFetch().get<GetAvailabilityForDateResponseType>(
    `/api/v1/product/${productId}/availability/check?date=${date}`
  )
}

export const getProductAvailabilityForDateWithCallToBook = async ({
  productId,
  date,
  isIntegratedPage = false,
}: {
  productId: string
  date: string
  isIntegratedPage?: boolean | undefined
}) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetAvailabilityForDateResponseType>(
      `/api/v1/public/product/${productId}/availability/check?date=${date}&ignore_call_to_book_closure=true`
    )
  }
  return await privateFetch().get<GetAvailabilityForDateResponseType>(
    `/api/v1/product/${productId}/availability/check?date=${date}&ignore_call_to_book_closure=true`
  )
}

export const getAllCustomers = async (filter = {}) => {
  return await privateFetch().get<GetCustomersResponseType>(
    `/api/v1/customers/${jsonToQueryString(filter)}`
  )
}

export const addCustomer = async (body: AddCustomerRequestType, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().post<AddCustomerResponseType>(`/api/v1/public/customers`, body)
  }
  return await privateFetch().post<AddCustomerResponseType>(`/api/v1/customers`, body)
}

export const getProductFields = async (productId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetProductFieldsResponseType>(
      `/api/v1/public/product/${productId}/fields`
    )
  }
  return await privateFetch().get<GetProductFieldsResponseType>(
    `/api/v1/product/${productId}/fields`
  )
}

export const getUnitTypeLabel = async (productId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().get<any>(`/api/v1/public/product/${productId}/unit-type-labels`)
  }
  return await privateFetch().get<any>(`/api/v1/product/${productId}/unit-type-labels`)
}

export const createCart = async (body: CreateCartRequestType, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().post<CreateCartResponseType>(`/api/v1/public/cart`, body)
  }
  return await privateFetch().post<CreateCartResponseType>(`/api/v1/cart`, body)
}

export const updateCart = async (
  cartId: string,
  body: CreateCartRequestType,
  isIntegratedPage?: boolean,
  config: AxiosRequestConfig = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<CreateCartResponseType>(
      `/api/v1/public/cart/${cartId}`,
      body,
      config
    )
  }
  return await privateFetch().post<CreateCartResponseType>(`/api/v1/cart/${cartId}`, body, config)
}

export const deleteCartItem = async (cartId: string, cartItemId: string) =>
  await privateFetch().post<CreateCartResponseType>(`/api/v1/cart/${cartId}/delete-item`, {
    id: cartItemId,
  })

export const deleteCartItems = async (cartId: string, cartItemIds: string[]) =>
  await privateFetch().post<CreateCartResponseType>(`/api/v1/cart/${cartId}/delete-items`, {
    items: cartItemIds,
  })

export const getCart = async (cartId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetCartResponseType>(`/api/v1/public/cart/${cartId}`)
  }
  return await privateFetch().get<GetCartResponseType>(
    `/api/v1/cart/${cartId}?includeMembershipDetails=1`
  )
}

export const addParticipantsToCart = async (
  cartId: string,
  body: AddParticipantsToCartRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<AddParticipantsToCartResponseType>(
      `/api/v1/public/cart/${cartId}/participants`,
      body
    )
  }
  return await privateFetch().post<AddParticipantsToCartResponseType>(
    `/api/v1/cart/${cartId}/participants`,
    body
  )
}

export const getParticipantsFromCart = async (cartId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetParticipantsFromCart>(
      `/api/v1/public/cart/${cartId}/participants`
    )
  }
  return await privateFetch().get<GetParticipantsFromCart>(`/api/v1/cart/${cartId}/participants`)
}

export const updateParticipantsInCart = async (
  cartId: string,
  body: UpdateParticipantsInCartRequestType
) =>
  await privateFetch().post<AddParticipantsToCartResponseType>(
    `/api/v1/cart/${cartId}/participants`,
    body
  )

export const getPerBookingFieldsOfCart = async (cartId: string) =>
  await privateFetch().get<GetPerBookingFieldsOfCartResponseType>(
    `/api/v1/cart/${cartId}/booking-fields`
  )

export const updatePerBookingFieldsOfCart = async (
  { body, cartId, cartItemId }: UpdatePerBookingFieldsOfCartRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    await publicFetch().post<UpdatePerBookingFieldsOfCartResponseType>(
      `/api/v1/public/cart/${cartId}/${cartItemId ? `${cartItemId}/` : ""}booking-fields`,
      body
    )
  } else {
    await privateFetch().post<UpdatePerBookingFieldsOfCartResponseType>(
      `/api/v1/cart/${cartId}/${cartItemId ? `${cartItemId}/` : ""}booking-fields`,
      body
    )
  }
}

export const getPerOrderFieldsOfOrder = async (orderId: string) =>
  await privateFetch().get<GetParticipantsFromCart>(`/api/v1/order/${orderId}/fields`)

export const createOrder = async (body: CreateOrderRequestType, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().post<CreateOrderResponseType>(`api/v1/public/order`, body)
  }
  return await privateFetch().post<CreateOrderResponseType>(`api/v1/order`, body)
}

export const getPerOrderFieldsOfCart = async (cartId: string) =>
  await privateFetch().get<GetParticipantsFromCart>(`/api/v1/cart/${cartId}/fields`)

export const updatePerOrderFieldsOfCart = async (
  cartId: string,
  body: UpdatePerOrderFieldsOfCartRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<UpdatePerOrderFieldsOfCartResponseType>(
      `/api/v1/public/cart/${cartId}/fields`,
      body
    )
  }
  return await privateFetch().post<UpdatePerOrderFieldsOfCartResponseType>(
    `/api/v1/cart/${cartId}/fields`,
    body
  )
}

export const getAllOrders = async (filter = {}) =>
  await privateFetch().get<GetAllOrdersResponseType>(`api/v1/order${jsonToQueryString(filter)}`)

export const applyPromocode = async (
  cartId: string,
  body: ApplyPromocodeRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<ApplyPromocodeResponseType>(
      `/api/v1/public/cart/${cartId}/apply-offer`,
      body
    )
  }
  return await privateFetch().post<ApplyPromocodeResponseType>(
    `/api/v1/cart/${cartId}/apply-offer`,
    body
  )
}

export const applyPromocodeInOrder = async (orderId: string, body: ApplyPromocodeRequestType) =>
  await privateFetch().post<ApplyPromocodeResponseType>(
    `/api/v1/order/${orderId}/apply-offer`,
    body
  )

export const removePromocode = async (cartId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().post<CreateCartResponseType>(
      `/api/v1/public/cart/${cartId}/remove-offer`
    )
  }
  return await privateFetch().post<CreateCartResponseType>(`/api/v1/cart/${cartId}/remove-offer`)
}

export const removePromocodeInOrder = async (orderId: string) =>
  await privateFetch().post<CreateCartResponseType>(`/api/v1/order/${orderId}/remove-offer`)

export const getOrderDetails = async (orderId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetOrderDetailsResponseType>(
      `api/v1/public/order/${orderId}?includeMembershipDetails=1&includeWaiverCount=1`
    )
  }
  return await privateFetch().get<GetOrderDetailsResponseType>(
    `api/v1/order/${orderId}?includeMembershipDetails=1&includeWaiverCount=1`
  )
}

export const updateOrder = async (body: UpdateOrderRequestType) =>
  await privateFetch().post<CreateCartResponseType>(`/api/v1/cart`, body)

export const saveCartToOrder = async (orderId: string, body: SaveCartToOrderRequestType) =>
  await privateFetch().post<UpdateOrderStatusResponseType>(
    `/api/v1/order/${orderId}/save-edits`,
    body
  )

export const discardCart = async (cartId: string) =>
  await privateFetch().delete<DiscardCartResponseType>(`/api/v1/cart/${cartId}`)

export const getInvoiceDetails = async (invoiceId: string) =>
  await privateFetch().get<GetOrderDetailsResponseType["invoice"]>(`api/v1/invoice/${invoiceId}`)

export const getOrderCheckInDetails = async (orderId: string) =>
  await privateFetch().get<GetOrderCheckInDetailsResponseType>(`api/v1/order/${orderId}/check-in`)

export const getPerBookingFieldsOfOrder = async (orderId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetPerBookingFieldsOfCartResponseType>(
      `/api/v1/public/order/${orderId}/booking-fields`
    )
  }
  return await privateFetch().get<GetPerBookingFieldsOfCartResponseType>(
    `/api/v1/order/${orderId}/booking-fields`
  )
}

export const getInvoiceDetailsForPdf = async (
  invoiceId: string,
  headers?: Record<string, string>
) =>
  await privateFetch().get<GetOrderDetailsResponseType["invoice"]>(`api/v1/invoice/${invoiceId}`, {
    headers,
  })

export const downloadInvoice = async (invoiceId: string, headers?: Record<string, string>) =>
  await privateFetch().get(`api/v1/invoice/${invoiceId}/pdf`, {
    responseType: "arraybuffer",
    headers,
  })

export const updateInvoiceNotes = async (invoiceId: string, body: { notes: string }) =>
  await privateFetch().put(`api/v1/invoice/${invoiceId}/notes`, body)

export const sendInvoiceToCustomer = async (
  invoiceId: string,
  body: { settingId: string; email: string }
) => await privateFetch().post(`api/v1/invoice/${invoiceId}/send`, body)

export const getPerOrderFields = async (orderId: string) =>
  await privateFetch().get<GetPerOrderFieldsResponseType>(`/api/v1/order/${orderId}/fields`)

export const updateParticipantCheckInStatus = async (
  participantId: string,
  body: UpdateParticipantCheckInStatusRequestType
) =>
  await privateFetch().post<UpdateParticipantCheckInStatusResponseType>(
    `/api/v1/booking-participant/${participantId}/check-in`,
    body
  )

export const updateOrderCheckInStatus = async (
  orderId: string,
  body: UpdateParticipantCheckInStatusRequestType
) =>
  await privateFetch().post<UpdateOrderCheckInStatusResponseType[]>(
    `/api/v1/booking/${orderId}/check-in`,
    body
  )

export const updateOrderStatus = async (orderId: string, body: UpdateOrderStatusRequestType) =>
  await privateFetch().post<UpdateOrderStatusResponseType>(
    `/api/v1/order/${orderId}/update-status`,
    body
  )

export const refundTransaction = async (transactionId: string, body: RefundFormAttributes) =>
  await privateFetch().post<GetAllOrdersResponseType>(
    `/api/v1/transactions/${transactionId}/refund`,
    body
  )

export const refund = async (orderId: string, body: RefundFormAttributes) =>
  await privateFetch().post<GetAllOrdersResponseType>(`/api/v1/order/${orderId}/refund`, body)

export const getRefundMethods = async (orderId: string) =>
  await privateFetch().get<GetRefundMethodsType[]>(`/api/v1/order/${orderId}/refund/methods`)

export const getTransactions = async (orderId: string, params = {}) =>
  await privateFetch().get<GetTransactionsResponseType>(
    `/api/v1/order/${orderId}/transaction${jsonToQueryString(params)}`
  )

export const getAllTransactions = async (orderId: string) =>
  await privateFetch().get(`/api/v1/order/${orderId}/all-transaction`)

export const payOrder = async (body: PayOrderRequestType, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().post<CreateOrderResponseType>(`/api/v1/public/order/payment`, body)
  }
  return await privateFetch().post<CreateOrderResponseType>(`/api/v1/order/payment`, body)
}

export const getWaiverDataByOrderId = async (orderId: string, filter = {}) =>
  await privateFetch().get(`/api/v1/order/${orderId}/waiver${jsonToQueryString(filter)}`)

export const getLiveCapacityByProductId = async (
  productId: string,
  body: GetLiveCapacityByProductIdPayload,
  isIntegratedPage: boolean,
  params = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<GetLiveCapacityByProductIdResponseType>(
      `/api/v1/public/product/${productId}/availability/live-capacity${jsonToQueryString(params)}`,
      body
    )
  }
  return await privateFetch().post<GetLiveCapacityByProductIdResponseType>(
    `/api/v1/product/${productId}/availability/live-capacity${jsonToQueryString(params)}`,
    body
  )
}

export const getLiveCapacityByProductIdForCallToBook = async (
  productId: string,
  body: GetLiveCapacityByProductIdPayload,
  isIntegratedPage: boolean,
  params = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<GetLiveCapacityByProductIdResponseType>(
      `/api/v1/public/product/${productId}/availability/live-capacity${jsonToQueryString(params)}`,
      body
    )
  }
  return await privateFetch().post<GetLiveCapacityByProductIdResponseType>(
    `/api/v1/product/${productId}/availability/live-capacity${jsonToQueryString(params)}`,
    body
  )
}

export const addGiftCardDetailsToCartItem = async (
  cartId: string,
  body: any,
  isIntegratedPage: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post(`/api/v1/public/cart/${cartId}/booking-fields`, body)
  }
  return await privateFetch().post(`/api/v1/cart/${cartId}/booking-fields`, body)
}

export const updateCancellationReasons = async (
  domainId: string,
  body: { cancellation_reasons: string[] }
) =>
  await privateFetch().post<DomainResponseType>(
    `/api/v1/domain/${domainId}/cancellation-reasons`,
    body
  )

export const getSavedCardsOfCustomer = async (customerId: string, integrationId?: string) =>
  await privateFetch().get<GetSavedCardsOfCustomerResponseType>(
    `/api/v1/customers/${customerId}/cards${integrationId ? `?integration_id=${integrationId}` : ""}`
  )

export const deleteSavedCardOfCustomer = async (customerId: string, cardId: string) =>
  await privateFetch().delete<DiscardCartResponseType>(
    `/api/v1/customers/${customerId}/cards/${cardId}`
  )

export const authorizeCardToHoldAmount = async ({
  orderId,
  orderItemId,
  body,
}: {
  orderId: string
  orderItemId?: string
  body: AuthorizeCardToHoldAmountRequestType
}) =>
  await privateFetch().post<AuthorizeCardToHoldAmountResponseType>(
    `/api/v1/order/${orderId}${orderItemId ? `/orderItem/${orderItemId}` : ""}/authorize-card`,
    body
  )

export const releasePaymentHold = async (paymentHoldId: string) =>
  await privateFetch().post<TransactionType>(`/api/v1/payment-holds/${paymentHoldId}/release`)

export const captureAuthorizedHoldAmount = async (
  transactionId: string,
  body?: { amount: string }
) =>
  await privateFetch().post<DiscardCartResponseType>(
    `/api/v1/transactions/${transactionId}/captureAuthorization`,
    body
  )

export const getDynamicRentalscheduling = async (
  productId: string,
  filter = {},
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().get(
      `/api/v1/public/product/${productId}/availability/rentals${jsonToQueryString(filter)}`
    )
  }
  return await privateFetch().get(
    `/api/v1/product/${productId}/availability/rentals${jsonToQueryString(filter)}`
  )
}

export const getDynamicRentalLiveCapacity = async (
  productId: string,
  body: any,
  isIntegratedPage: boolean,
  params = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().post(
      `/api/v1/public/product/${productId}/availability/live-capacity/range${jsonToQueryString(params)}`,
      body
    )
  }
  return await privateFetch().post(
    `/api/v1/product/${productId}/availability/live-capacity/range${jsonToQueryString(params)}`,
    body
  )
}

export const getCallToBookDynamicRentalLiveCapacity = async (
  productId: string,
  body: any,
  isIntegratedPage: boolean,
  params = {}
) => {
  if (isIntegratedPage) {
    return await publicFetch().post(
      `/api/v1/public/product/${productId}/availability/live-capacity/range${jsonToQueryString(params)}`,
      body
    )
  }
  return await privateFetch().post(
    `/api/v1/product/${productId}/availability/live-capacity/range${jsonToQueryString(params)}`,
    body
  )
}

export const capturePaymentHold = async (paymentHoldId: string, body?: { amount: string }) =>
  await privateFetch().post<DiscardCartResponseType>(
    `/api/v1/payment-holds/${paymentHoldId}/capture`,
    body
  )

export const getPaymentHoldsList = async (orderId: string, orderItemId?: string) =>
  await privateFetch().get<IPaymentHoldType[]>(
    `/api/v1/order/${orderId}${orderItemId ? `/orderItem/${orderItemId}` : ""}/payment-holds`
  )

export const validateGiftcardCode = async (
  cartId: string,
  body: ApplyPromocodeRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<ValidateGiftcardCodeResponseType>(
      `/api/v1/public/cart/${cartId}/validate-offer`,
      body
    )
  }
  return await privateFetch().post<ValidateGiftcardCodeResponseType>(
    `/api/v1/cart/${cartId}/validate-offer`,
    body
  )
}

export const validateOrderGiftcardCode = async (
  orderId: string,
  body: ApplyPromocodeRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<ValidateGiftcardCodeResponseType>(
      `/api/v1/public/order/${orderId}/validate-offer`,
      body
    )
  }
  return await privateFetch().post<ValidateGiftcardCodeResponseType>(
    `/api/v1/order/${orderId}/validate-offer`,
    body
  )
}

export const createEditExpenseDiscount = async (
  cartId: string,
  body: CreateEditExpenseDiscountRequestType
) => await privateFetch().post<CreateCartResponseType>(`/api/v1/cart/${cartId}/expense`, body)

export const calculateOrderTip = async (orderId: string, body: CalculateOrderTipRequestType) =>
  await privateFetch().post<CalculateOrderTipResponseType>(
    `/api/v1/order/${orderId}/calculate-tip`,
    body
  )

export const updatePerOrderFieldsOfOrder = async (
  orderId: string,
  body: UpdatePerOrderFieldsOfOrderRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<GetPerOrderFieldsResponseType>(
      `/api/v1/public/order/${orderId}/update-fields`,
      body
    )
  }
  return await privateFetch().post<GetPerOrderFieldsResponseType>(
    `/api/v1/order/${orderId}/update-fields`,
    body
  )
}

export const updatePerBookingFieldsOfOrder = async (
  orderId: string,
  body: UpdatePerBookingFieldsOfOrderRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<GetPerOrderFieldsResponseType>(
      `/api/v1/public/order/${orderId}/update-booking-fields`,
      body
    )
  }
  return await privateFetch().post<GetPerOrderFieldsResponseType>(
    `/api/v1/order/${orderId}/update-booking-fields`,
    body
  )
}

export const addExpenseDiscountToOrderItem = async (
  orderId: string,
  orderItemId: string,
  body: AddExpenseDiscountToOrderItemRequestType
) =>
  await privateFetch().post<GetOrderDetailsResponseType>(
    `/api/v1/order/${orderId}/order-item/${orderItemId}/add-expense-discount`,
    body
  )

export const updateExpenseDiscountOrderItem = async (
  orderId: string,
  orderItemId: string,
  body: AddExpenseDiscountToOrderItemRequestType
) =>
  await privateFetch().post<GetOrderDetailsResponseType>(
    `/api/v1/order/${orderId}/order-item/${orderItemId}/update-expense-discount`,
    body
  )

export const updatePriceOfOrderItem = async (
  orderId: string,
  orderItemId: string,
  body: UpdatePriceOfOrderItemRequestType
) =>
  await privateFetch().post<GetOrderDetailsResponseType>(
    `/api/v1/order/${orderId}/order-item/${orderItemId}/update-price`,
    body
  )

export const deleteNonBookableOrderItem = async (orderId: string, orderItemId: string) =>
  await privateFetch().delete<GetOrderDetailsResponseType>(
    `/api/v1/order/${orderId}/order-item/${orderItemId}/delete`
  )

export const addAddonToOrderItem = async (
  orderId: string,
  orderItemId: string,
  body: AddAddonToOrderItemRequestType
) =>
  await privateFetch().post<GetOrderDetailsResponseType>(
    `/api/v1/order/${orderId}/order-item/${orderItemId}/add-addon`,
    body
  )

export const addMultipleAddonToOrderItem = async (
  orderId: string,
  orderItemId: string,
  body: AddMultipleAddonToOrderItemRequestType
) =>
  await privateFetch().post<GetOrderDetailsResponseType>(
    `/api/v1/order/${orderId}/order-item/${orderItemId}/add-multiple-addon`,
    body
  )

export const updateAddonOfOrderItem = async (
  orderId: string,
  orderItemId: string,
  body: UpdateAddonOfOrderItemRequestType
) =>
  await privateFetch().post<GetOrderDetailsResponseType>(
    `/api/v1/order/${orderId}/order-item/${orderItemId}/update-addon`,
    body
  )

export const updateQuantityOfOrderItem = async (
  orderId: string,
  orderItemId: string,
  body: UpdateQuantityOfOrderItemRequestType
) =>
  await privateFetch().post<GetOrderDetailsResponseType>(
    `/api/v1/order/${orderId}/order-item/${orderItemId}/update-quantity`,
    body
  )

export const getParticipantsOfOrderItem = async (orderId: string, orderItemId: string) =>
  await privateFetch().get<GetParticipantsOfOrderItemResponseType>(
    `/api/v1/order/${orderId}/order-item/${orderItemId}/participants`
  )

export const getPerParticipantFieldsOfOrder = async (
  orderId: string,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().get<GetPerParticipantFieldsOfOrderResponseType>(
      `/api/v1/public/order/${orderId}/participant-fields`
    )
  }
  return await privateFetch().get<GetPerParticipantFieldsOfOrderResponseType>(
    `/api/v1/order/${orderId}/participant-fields`
  )
}

export const updatePerParticipantFieldsOfOrder = async (
  orderId: string,
  body: UpdatePerParticipantFieldsOfOrderRequestType[],
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<GetPerParticipantFieldsOfOrderResponseType>(
      `/api/v1/public/order/${orderId}/update-participant-fields`,
      body
    )
  }
  return await privateFetch().post<GetPerParticipantFieldsOfOrderResponseType>(
    `/api/v1/order/${orderId}/update-participant-fields`,
    body
  )
}

export const updateOrderCustomer = async (orderId: string, body: UpdateOrderCustomerRequestType) =>
  await privateFetch().post<GetOrderDetailsResponseType>(
    `/api/v1/order/${orderId}/change-customer`,
    body
  )

export const updateOrderAffiliate = async (
  orderId: string,
  body: UpdateOrderAffiliateRequestType,
  config?: AxiosRequestConfig
) =>
  await privateFetch().post<GetOrderDetailsResponseType>(
    `/api/v1/order/${orderId}/change-affiliate`,
    body,
    config
  )

export const removeOrderAffiliate = async (orderId: string) =>
  await privateFetch().post<GetOrderDetailsResponseType>(
    `/api/v1/order/${orderId}/remove-affiliate`
  )

export const getActionsLogs = async (filter = {}) =>
  await privateFetch().get<GetActionsLogResponseType>(
    `/api/v1/activity-logs${jsonToQueryString(filter)}`
  )

export const getMembershipDiscounts = async (productId: string) =>
  await privateFetch().get<GetMembershipDiscountsResponseType>(
    `/api/v1/product/${productId}/membership/discount?paginate=0`
  )

export const applyMembershipToCart = async (
  cartId: string,
  body: ApplyMembershipToCartRequestType,
  isIntegratedPage?: boolean
) => {
  if (isIntegratedPage) {
    return await publicFetch().post<ApplyMembershipToCartResponseType>(
      `/api/v1/public/cart/${cartId}/membership/apply`,
      body
    )
  }
  return await privateFetch().post<ApplyMembershipToCartResponseType>(
    `/api/v1/cart/${cartId}/membership/apply`,
    body
  )
}

export const removeMembershipFromCart = async (cartId: string, isIntegratedPage?: boolean) => {
  if (isIntegratedPage) {
    return await publicFetch().post(`/api/v1/public/cart/${cartId}/membership/remove`)
  }
  return await privateFetch().post(`/api/v1/cart/${cartId}/membership/remove`)
}

export const applyMembershipToOrder = async (
  orderId: string,
  body: ApplyMembershipToCartRequestType
) =>
  await privateFetch().post<ApplyMembershipToCartResponseType>(
    `/api/v1/order/${orderId}/membership/apply`,
    body
  )

export const removeMembershipFromOrder = async (orderId: string) =>
  await privateFetch().post(`/api/v1/order/${orderId}/membership/remove`)

export const getCustomerMembershipDetails = async (id: string) =>
  await privateFetch().get<CustomerMembershipResponseType>(`/api/v1/customer-membership/${id}`)

export const getMembershipUsage = async (id: string) =>
  await privateFetch().get<MembershipUsageResponseType>(`/api/v1/customer-membership/usage/${id}`)

export const issueGiftcardForRefund = async (
  orderId: string,
  body: IssueGiftcardForRefundRequestType
) => await privateFetch().post(`/api/v1/order/${orderId}/refund/as-gift-card`, body)

export const getUnitsWithLiveCapacity = async (
  productId: string,
  body: getUnitsWithLiveCapacityRequestType
) =>
  await privateFetch().post(
    `/api/v1/product/${productId}/availability/get-units-with-live-capacity`,
    body
  )
